/**
 * Domain types for the irrigation scheduler.
 *
 * Units are US customary throughout: depth in inches, precipitation rate in
 * inches/hour, temperature in °F, wind in mph. Keep this consistent — Rachio
 * and Open-Meteo (imperial mode) both align with it.
 *
 * Enums are defined as `const` arrays plus a derived union type so the same
 * declaration serves both runtime validation/UI (the array) and the type
 * system (the union).
 */
export const PLANT_TYPES = [
    'COOL_SEASON_GRASS',
    'WARM_SEASON_GRASS',
    'SHRUBS',
    'TREES',
    'XERISCAPE',
    'ANNUALS',
    'PERENNIALS',
    'VEGETABLE_GARDEN',
];
export const SOIL_TYPES = [
    'SAND',
    'LOAMY_SAND',
    'SANDY_LOAM',
    'LOAM',
    'SILT_LOAM',
    'CLAY_LOAM',
    'CLAY',
];
export const SUN_EXPOSURES = ['FULL_SUN', 'PARTIAL_SHADE', 'FULL_SHADE'];
export const SLOPES = ['FLAT', 'SLIGHT', 'MODERATE', 'STEEP'];
export const NOZZLE_TYPES = [
    'FIXED_SPRAY',
    'ROTOR',
    'ROTARY_NOZZLE',
    'DRIP',
    'BUBBLER',
];
/** Coarse drip layouts users pick instead of measuring emitter geometry. */
export const DRIP_LAYOUTS = ['INLINE_DRIPLINE', 'POINT_DENSE', 'POINT_SPARSE', 'MICRO_SPRAY'];
export const SCHEDULE_MODES = ['SMART', 'FIXED'];
/**
 * Why a zone isn't watering in a session. `NEW_SEED` is the odd one out: it is
 * not a deferral at all but a hand-off: the zone is being misted on its own
 * track (see {@link NewSeedProgram}), so the session must not also water it.
 */
export const SKIP_REASONS = [
    'RAIN',
    'RAIN_AHEAD',
    'FREEZE',
    'WIND',
    'DAY_RESTRICTION',
    'PAUSED',
    'WINDOW_FULL',
    'NEW_SEED',
];
export const RUN_STATUSES = [
    'PLANNED',
    'RUNNING',
    'COMPLETED',
    'SKIPPED',
    'FAILED',
];
export const PLANNER_OUTCOMES = [
    'PLANNED',
    'ALREADY_PLANNED',
    'WINDOW_CLOSED',
    'NO_LOCATION',
    'WINTERIZED',
    'FAILED',
];
/**
 * The two per-session slots a plan-summary email can occupy: the evening
 * heads-up, and the just-before-watering notice sent only when the plan changed
 * since that heads-up.
 */
export const EMAIL_SLOTS = ['EVENING', 'PRE_WINDOW'];
/**
 * Why plan email was switched off without the user asking. BOUNCE means the
 * address is permanently undeliverable; COMPLAINT means the recipient marked
 * our mail as spam. Both must stop further sending: continuing to mail either
 * one is what wrecks a sender's reputation with the receiving providers.
 */
export const EMAIL_DISABLED_REASONS = ['BOUNCE', 'COMPLAINT'];
//# sourceMappingURL=types.js.map